import { useEffect, useRef, useState } from 'react'
import { listViews } from '../api'
import { describeView } from './SavedViews'
import { canPickFiles, clearRecent, fileFromHandle, getHandle, loadRecent } from '../handles'
import { bytes } from '../format'

/**
 * The landing page: pick a CSV and go. Replaces the server version's path
 * browser — a browser tab cannot walk the filesystem, and should not: the file
 * the user hands over is the only one this app can ever see, and it is read in
 * place, never uploaded.
 *
 * Where the browser can remember file handles (Chrome, Edge), saved views and
 * recent files reopen with a click. Elsewhere they still appear, and clicking
 * one asks for the file again.
 */

const ACCEPT = '.csv,.tsv,.txt,.psv,text/csv'

const PICKER_TYPES = [
  {
    description: 'Spreadsheets (CSV)',
    accept: { 'text/csv': ['.csv', '.tsv', '.txt', '.psv'] },
  },
]

export function FilePicker({ onOpen, busy, error }) {
  const inputRef = useRef(null)
  const [views, setViews] = useState([])
  const [recent, setRecent] = useState(loadRecent)
  const [over, setOver] = useState(false)
  // A saved view whose file has to be re-picked: applied to the next file chosen.
  const [pendingView, setPendingView] = useState(null)

  // Saved views are the fastest way back in, so they lead on this screen.
  useEffect(() => {
    listViews()
      .then((r) => setViews(r.views))
      .catch(() => setViews([]))
  }, [])

  /** Open through the native picker, keeping the handle where the browser has one. */
  const pick = async (view = null) => {
    if (canPickFiles) {
      try {
        const [handle] = await window.showOpenFilePicker({ types: PICKER_TYPES, excludeAcceptAllOption: false })
        const file = await handle.getFile()
        setPendingView(null)
        onOpen(file, view, handle)
      } catch {
        // The picker was dismissed; keep the pending view for the input fallback.
      }
      return
    }
    setPendingView(view)
    inputRef.current?.click()
  }

  const onInput = (e) => {
    const file = e.target.files?.[0]
    e.target.value = ''
    if (!file) return
    const view = pendingView
    setPendingView(null)
    onOpen(file, view, null)
  }

  /** A saved view or recent entry: reopen from the stored handle, or re-pick. */
  const reopen = async (name, view = null) => {
    const handle = await getHandle(name)
    if (handle) {
      const file = await fileFromHandle(handle)
      if (file) {
        onOpen(file, view, handle)
        return
      }
    }
    pick(view)
  }

  return (
    <div className="landing">
      <div className="landing-card">
        <h1>
          Read any spreadsheet,
          <br />
          however <em>big</em> it is.
        </h1>
        <p className="lede">
          Pick a CSV and it opens straight away — a million rows or twenty million, it is read a piece
          at a time rather than loaded whole. Narrow it down by any column, sort out the duplicates,
          then export what is left. The file stays on your machine: nothing is uploaded, and everything
          you save is saved in this browser only.
        </p>

        <div
          className={`drop landing-drop${over ? ' over' : ''}${busy ? ' busy' : ''}`}
          onDragOver={(e) => {
            e.preventDefault()
            setOver(true)
          }}
          onDragLeave={() => setOver(false)}
          onDrop={(e) => {
            e.preventDefault()
            setOver(false)
            const file = e.dataTransfer.files?.[0]
            if (file) onOpen(file, null, null)
          }}
        >
          <button className="drop-button" type="button" disabled={busy} onClick={() => pick()}>
            {busy ? (
              <span className="drop-text">Opening…</span>
            ) : (
              <span className="drop-text">
                <b>Drop a CSV here</b>
                <span className="drop-meta">or click to choose one</span>
              </span>
            )}
          </button>
        </div>
        <input
          ref={inputRef}
          type="file"
          accept={ACCEPT}
          style={{ display: 'none' }}
          onChange={onInput}
        />

        {error && <div className="alert">{error}</div>}
        {pendingView && !busy && (
          <div className="alert">
            Pick the file “{pendingView.path}” to reopen “{pendingView.name}”.
          </div>
        )}

        {views.length > 0 && (
          <div className="landing-section">
            <span className="label">Pick up where you left off</span>
            <ul className="view-list">
              {views.map((view) => (
                <li key={view.id}>
                  <button
                    className="view-item"
                    onClick={() => reopen(view.path, view)}
                    title={canPickFiles ? view.path : `${view.path} — you will be asked to pick the file`}
                  >
                    <span className="view-name">
                      {view.name}
                      <span className="view-file">{view.path}</span>
                    </span>
                    <span className="view-meta">{describeView(view)}</span>
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}

        {recent.length > 0 && (
          <div className="landing-section recent">
            <span className="label">Recently opened</span>
            {recent.map((r) => (
              <button
                key={r.name}
                className="chip"
                onClick={() => reopen(r.name)}
                title={r.size != null ? `${r.name} · ${bytes(r.size)}` : r.name}
              >
                {r.name}
              </button>
            ))}
            <button
              className="chip subtle"
              onClick={() => {
                clearRecent()
                setRecent([])
              }}
            >
              clear
            </button>
          </div>
        )}

        <p className="footnote">
          Everything runs in this tab. Files are read in place and never uploaded; saved views and
          recents live in this browser alone. The one outside call is the optional email check, which
          asks a public DNS resolver about domain names — never about your rows.
        </p>
      </div>
    </div>
  )
}
